import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { LoanLite } from '../modules/Loan/types/response/LoanResponseLite';
import { RootState } from './store';
import { LoanSummary } from '../modules/Loan/types/response/Summary';

type PageIndex = number;

export interface CursorPage<T> {
	items: T[];
}

export interface LoanState {
	// current view
	page: number;
	rowsPerPage: number;

	// cursor paging
	cursors: Array<number | null>; // cursors[0] = null (start), cursors[n] = cursor to fetch page n
	pageCache: Record<PageIndex, CursorPage<LoanLite>>;
	nextCursorByPage: Record<PageIndex, number | null>;

	// optional virtual count for table UX
	virtualCount: number; // computed as (maxLoadedPages * rpp) + (hasNext ? 1 : 0)

	summary?: LoanSummary | null;
}

const initialState: LoanState = {
	page: 0,
	rowsPerPage: 50,
	cursors: [null], // start
	pageCache: {},
	nextCursorByPage: {},
	virtualCount: 0,
	summary: null,
};

const slice = createSlice({
	name: 'loans',
	initialState,
	reducers: {
		resetLoanState: () => initialState,

		resetCursorPaging(state) {
			state.page = 0;
			state.cursors = [null];
			state.pageCache = {};
			state.nextCursorByPage = {};
			state.virtualCount = 0;
		},

		setLoanRowsPerPage(state, action: PayloadAction<number>) {
			state.rowsPerPage = action.payload;
		},

		setLoanPage(state, action: PayloadAction<number>) {
			state.page = action.payload;
		},

		// store a page result + next cursor
		setLoanPageData(
			state,
			action: PayloadAction<{ page: number; items: LoanLite[]; nextCursor?: number | null }>,
		) {
			const { page, items, nextCursor } = action.payload;

			state.pageCache[page] = { items };
			state.nextCursorByPage[page] = nextCursor ?? null;

			// ensure cursors array has entry for page+1 (what we will pass when fetching the next page)
			// cursors[page] is the cursor used to fetch this page (already set externally)
			// the server returns next_cursor to fetch the NEXT page:
			state.cursors[page + 1] = nextCursor ?? null;

			// recompute a virtual count so the table has something to render pages with
			const maxLoadedPage = Math.max(0, ...Object.keys(state.pageCache).map(n => +n));
			const hasNext = state.nextCursorByPage[maxLoadedPage] != null;
			state.virtualCount = (maxLoadedPage + 1) * state.rowsPerPage + (hasNext ? 1 : 0);
		},

		// keep for summary
		setLoanSummary(state, action: PayloadAction<LoanSummary | null | undefined>) {
			state.summary = action.payload ?? null;
		},
	},
});

export const {
	resetLoanState,
	resetCursorPaging,
	setLoanRowsPerPage,
	setLoanPage,
	setLoanPageData,
	setLoanSummary,
} = slice.actions;

export default slice.reducer;

// selectors
export const selectLoanPage = (s: RootState) => s.loans.page;
export const selectLoanRowsPerPage = (s: RootState) => s.loans.rowsPerPage;
export const selectLoanSummary = (s: RootState) => s.loans.summary;

export const selectLoanItems = (s: RootState) => {
	const p = s.loans.page;
	return s.loans.pageCache[p]?.items ?? [];
};
export const selectLoanCount = (s: RootState) => s.loans.virtualCount;
export const selectLoanCursorForPage = (p: number) => (s: RootState) => s.loans.cursors[p] ?? null;
export const selectHasCachedPage = (p: number) => (s: RootState) => Boolean(s.loans.pageCache[p]);
